import { useEffect, useMemo, useState } from "react";
import { getBankAccounts } from "../services/api";
import type { IBankAccountPaymentMethod } from "../interfaces/payment-method";
import { useSessionStore } from "../store/useSessionStore";

export interface UseBankAccountsState {
  accounts: IBankAccountPaymentMethod[];
  loading: boolean;
  error: boolean;
}

/**
 * Loads the claimer's saved payout bank accounts, narrowed to the destination
 * country of the claim — offering an account from another corridor would never
 * be payable on this bind.
 */
export const useBankAccounts = (
  destinationCountry: string
): UseBankAccountsState => {
  const linkId = useSessionStore((s) => s.session.data?.linkId ?? null);

  const [accounts, setAccounts] = useState<IBankAccountPaymentMethod[]>([]);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!linkId) return;
    let active = true;
    getBankAccounts(linkId)
      .then((result) => active && setAccounts(result))
      .catch(() => active && setError(true))
      .finally(() => active && setPending(false));
    return () => {
      active = false;
    };
  }, [linkId]);

  const forCountry = useMemo(() => {
    const country = destinationCountry.trim().toUpperCase();
    if (!country) return [];
    return accounts.filter(
      (a) => (a.countryCode || "").toUpperCase() === country
    );
  }, [accounts, destinationCountry]);

  return {
    accounts: forCountry,
    loading: Boolean(linkId) && pending,
    error,
  };
};
